import {Box, Button, styled} from "@mui/material";
import Base from "../../../../components/base";
import {buildPath, formatDateInfo, getFasciaRange, scale} from "../../../../utils";
import BaseHeader from "../../../../components/base/header";
import BaseTitle from "../../../../components/base/title";
import LABEL from "../../../../resources/label";
import {useLocation, useNavigate, useParams} from "react-router-dom";
import DateDetailedCard from "../../../../components/date-detailed-card";
import Text from "../../../../components/text";
import theme, {FONTS} from "../../../../constants/theme";
import React, {useEffect, useState} from "react";
import DateRowInfo from "../../../../components/date-row-info";
import CustomSvg from "../../../../components/custom-svg";
import {GOCCIA_RIFLESSO} from "../../../../constants/svgs";
import {NewDonorRequest, RaccoltaResponse} from "../../../../types";
import {fetchRaccoltaById} from "../../../../features/operator";
import { QueryBuilder } from "@mui/icons-material";
import ThemedText from "../../../../components/text";
import FullScreenDialog from "../../../../components/full-screen-dialog";
import {useMediaQuery} from "@mui/system";

const Wrapper = styled(Box)({

});

const ContentWrapper = styled(Box)({
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-around",
  marginTop: scale(65),
  height: scale(480 + 138),
  width: scale(320 + 40 + 753.14),
  [theme.breakpoints.down('sm')]: {
    width: '100vw',
  },
});

const DonorsWrapper = styled(Box)({
  display: "flex",
  flexDirection: "column",
  position: 'relative',
  top: scale(20),
  width: scale(730),
  height: scale(79 + 515),
  gap: scale(20),
});

const ChoiceRow = styled(Box)({
  display: 'flex',
  flexDirection: "column",
  width: scale(654.5),
  height: scale(213),
  marginTop: scale(10),
  [theme.breakpoints.down('sm')]: {
    marginTop: 0,
  },
});

const DetailBand = styled(Box)({
  display: 'flex',
  flexDirection: "row",
  gap: scale(40),
  marginTop: scale(28),
  [theme.breakpoints.down('sm')]: {
    justifyContent: "center",
    alignItems: 'center',
    flexDirection: "column",
    marginTop: 0,
  },
});

const TypeGroup = styled(Box)({
  display: 'flex',
  flexDirection: "row",
  width: scale(34.33 + 180),
  [theme.breakpoints.down('sm')]: {
    alignSelf: "center",
    marginRight: scale(30),
    display: 'flex',
    flexDirection: "column",
    gap: scale(20),
  },
});

const ButtonGroup = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  alignSelf: 'flex-start',
  columnGap: scale(11),
  marginTop: 'auto',
  marginLeft: scale(80),
  marginBottom: scale(10),
  [theme.breakpoints.down('sm')]: {
    alignSelf: 'center',
    marginLeft: 0,
    marginRight: scale(54),
    marginBottom: scale(16),
  },
});

const ThemedButton = styled(Button)({
  borderRadius: scale(30),
  width: scale(160),
  height: scale(52),
  marginTop: scale(13),
  boxShadow: 'none',
  '&:hover': {
    boxShadow: 'none',
  },
  '&.Mui-disabled': {
    backgroundColor: theme.palette.lightGray.main,
    cursor: 'not-allowed',
  },
});

const OperatoreAggiungiDonatore = () => {
  const { raccoltaId } = useParams<{ raccoltaId: string }>();
  const navigate = useNavigate();
  const [raccolta, setRaccolta] = useState<RaccoltaResponse | null>(null)
  const [selectedType, setSelectedType] = useState<'sangue' | 'plasma' | null>(null);
  const [selectedFascia, setSelectedFascia] = useState<number | null>(null);
  const [showNotaDialog, setShowNotaDialog] = useState(false);
  const [partialDonorInfo, setPartialDonorInfo] = useState<Partial<NewDonorRequest> | null>(null)
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [mobileStep, setMobileStep] = useState<1 | 2 | 3>(1);

  const location = useLocation();
  const path = location.pathname;

  const isOperatore = path.includes('operatore');
  const nextUrl = isOperatore ? `/operatore/dettaglio-raccolta/${raccoltaId}/aggiungi-donatore/selezione-orari` : `/donatore/raccolta/${raccoltaId}/selezione-orari`


  const handleFasciaClick = (fasciaIndex: number) => {
    setSelectedFascia(prev => (prev === fasciaIndex ? null : fasciaIndex));
  };

  useEffect(() => {
    fetchRaccoltaById(Number(raccoltaId)).then((res) => {
      setRaccolta(res);
      if (res.fasceETurni.length === 1) {
        setSelectedFascia(0)
      }
      if (res.type !== 'plasma & sangue') {
        setSelectedType(res.type)
      }
      if (res.nota && res.nota.length > 0) {
        setShowNotaDialog(true)
      }
      const stored = sessionStorage.getItem('newDonorInfo')
      if (stored) {
        setPartialDonorInfo(JSON.parse(stored))
      }
    })
  }, [raccoltaId])

  if (!raccolta) {
    return null;
  }
  const props = isMobile
    ? {
      isMobile: true as const,
      type: raccolta.type,
    }
    : {
      isMobile: false as const,
    };

  const dateInfo = formatDateInfo(raccolta.date)

  const storePartialDonorInfo = () => {
    if (selectedFascia != null && selectedType != null) {
      const info: Partial<NewDonorRequest> = {
        ...partialDonorInfo,
        id_raccolta: raccolta.id,
        id_fascia: raccolta.fasceETurni.at(selectedFascia)?.fascia.id,
        type: selectedType
      }
      sessionStorage.setItem('newDonorInfo', JSON.stringify(info));
    }
  }

  const isDisabled = () => {
    if (isMobile) {
      return (mobileStep === 2 && selectedType === null) || (mobileStep === 3 && selectedFascia === null)
    } else {
      return (selectedFascia === null || selectedType === null)
    }
  }

  return (
    <>
      <FullScreenDialog
        title={"NOTA DELL'OPERATORE"}
        content={raccolta.nota}
        open={showNotaDialog}
        onClose={() => setShowNotaDialog(false)}
        onConfirm={() => setShowNotaDialog(false)}
        hasTextInput={false}
        colorStyle={'red'}
        cancelText={'ANNULLA'}
        confirmText={'CONTINUA'}
      />
      <Wrapper>
        <Base style={{ height: isMobile ? 'auto' : scale(850), width: isMobile ? undefined : scale(1200)}}>
          <BaseHeader mobileStep={mobileStep} setMobileStep={(value) => setMobileStep(value)}/>
          <BaseTitle
            title={
              isOperatore ? LABEL.DETAIL_DONATION :
                !isMobile ? 'Hai Scelto di donare' :
                  (mobileStep === 1 ? 'Hai Scelto di donare' :
                    (mobileStep === 2 ? 'Cosa vuoi donare?' : 'Quando vuoi donare?'))
            }
          />
          <ContentWrapper>
            {(!isMobile || (isMobile && mobileStep === 1)) && (<Box style={{display: 'flex', flexDirection: 'column', gap: scale(20)}}>
              <DateDetailedCard
                {...props}
                dayName={dateInfo.weekday}
                dayNumber={dateInfo.day.toString()}
                month={dateInfo.month.concat('\n')}
                year={dateInfo.year.toString()}
                colorStyle={"secondary"}
                city={raccolta.name}
                firstHour={getFasciaRange(raccolta, 0)}
                secondHour={getFasciaRange(raccolta, 1)}
                address={raccolta.address}
                sx={{paddingBottom: raccolta.name.length > 14 ? scale(20) : 0}}
              />
              {!isMobile && (raccolta.type === 'sangue' || raccolta.type === 'plasma & sangue') && (<DateRowInfo
                IconComponent={
                  <CustomSvg
                    svg={GOCCIA_RIFLESSO}
                    sx={{
                      zIndex: 2,
                      fontSize: scale(36),
                    }}
                    color={'white'}
                  />
                }
                bgColor={theme.palette.blood.main}
                colorStyle={'primary'}
                firstRowText={(raccolta.postiDisponibili ? raccolta.postiDisponibili.toString().concat(' ') : '') + 'Postazioni'}
                secondRowText={'Sangue'}
              />)}
              {!isMobile && (raccolta.type === 'plasma' || raccolta.type === 'plasma & sangue') && (<DateRowInfo
                IconComponent={
                  <CustomSvg
                    svg={GOCCIA_RIFLESSO}
                    sx={{
                      zIndex: 2,
                      fontSize: scale(36),
                    }}
                    color={'white'}
                  />
                }
                bgColor={theme.palette.plasma.main}
                colorStyle={'primary'}
                firstRowText={(raccolta.postiDisponibili ? raccolta.postiDisponibili.toString().concat(' ') : '') + 'Postazioni'}
                secondRowText={'Plasma'}
              />)}
              {isMobile && mobileStep === 1 && (
                <ButtonGroup sx={{
                  [theme.breakpoints.down('sm')]: {
                    display: 'flex',
                    justifyContent: 'center',
                    alignSelf: 'flex-start',
                    columnGap: scale(11),
                    marginTop: 'auto',
                    marginLeft: scale(80),
                    marginBottom: scale(20),
                  },
                }}>
                  <ThemedButton
                    variant="contained"
                    onClick={() => {
                      storePartialDonorInfo()
                      selectedType == null ? setMobileStep(2) : setMobileStep(3)
                    }}
                  >
                    <ThemedText text={'conferma'} fontWeight={600} fontSize={scale(18)} fontFamily={FONTS.MONTSERRAT}/>
                  </ThemedButton>
                </ButtonGroup>
              )}
            </Box>)}

            {(!isMobile || (isMobile && mobileStep !== 1)) && (<DonorsWrapper>
              {(!isMobile || (isMobile && mobileStep === 2)) && (<ChoiceRow>
                <Box style={{display: 'flex', flexDirection: 'column'}}>
                  {!isMobile && (<Text
                    text={'Cosa vuoi donare?'}
                    color={theme.palette.text.secondary}
                    fontFamily={FONTS.MONTSERRAT}
                    fontWeight={700}
                    fontSize={scale(24)}
                    sx={{textTransform: 'uppercase', alignSelf: 'flex-start'}}
                  />)}
                  {!isMobile && (<Box
                    sx={{
                      backgroundColor: theme.palette.text.secondary,
                      height: scale(3),
                      width: '100%',
                      marginTop: scale(10),
                    }}
                  />)}
                </Box>
                <DetailBand>
                  {!isMobile && (<Text
                    text={'Seleziona la tipologia di donazione che vuoi effettuare.'}
                    color={theme.palette.ceramic.contrastText}
                    fontFamily={FONTS.MONTSERRAT}
                    fontWeight={400}
                    fontSize={scale(20)}
                    sx={{alignSelf: 'flex-start', width: scale(280)}}
                  />)}
                  <TypeGroup>
                    {['sangue', 'plasma'].map((type) => {
                      const isActive = selectedType === type;
                      const isVisible = raccolta.type === type || raccolta.type === 'plasma & sangue';

                      if (!isVisible) return null;

                      const color = isActive
                        ? (type === 'sangue' ? theme.palette.blood.main : theme.palette.plasma.main)
                        : theme.palette.grey[400]; // colore grigio per non selezionato

                      return (
                        <Box
                          key={type}
                          onClick={() => setSelectedType(isActive ? null : (type as 'sangue' | 'plasma'))}
                          sx={{
                            cursor: 'pointer',
                            transition: 'all 0.8s',
                            [theme.breakpoints.down('sm')]: {
                              display: 'flex',
                              backgroundColor: color,
                              height: scale(200),
                              width: scale(200),
                              alignItems: 'center',
                              justifyContent: 'center',
                              borderRadius: scale(20),
                              WebkitTapHighlightColor: 'transparent',  // iOS
                              touchAction: 'manipulation',              // Android e altri
                              userSelect: 'none',                       // Previene selezione testo (opzionale)
                              WebkitUserSelect: 'none',
                              MozUserSelect: 'none',
                              msUserSelect: 'none',
                            },
                          }}
                        >
                          <DateRowInfo
                            IconComponent={
                              <CustomSvg
                                svg={GOCCIA_RIFLESSO}
                                sx={{
                                  zIndex: 2,
                                  fontSize: scale(60),
                                }}
                                color="white"
                              />
                            }
                            bgColor={color}
                            colorStyle={isMobile ? 'secondary' : "primary"}
                            firstRowText={type === 'sangue' ? 'Sangue' : 'Plasma'}
                            secondRowText={isMobile ? (type === 'sangue' ? '10 minuti' : '45 minuti') : ''}
                            iconSx={{width: scale(90), height: scale(90), borderRadius: scale(18), transition: isMobile ? 'all 0.8s' : undefined}}
                            sx={{flexDirection: 'column', width: scale(170), alignItems: 'center'}}
                            textSx={{width: scale(170), textAlign: 'center', fontWeight: 700}}
                          />
                        </Box>
                      );
                    })}
                  </TypeGroup>
                </DetailBand>
              </ChoiceRow>)}
              {(!isMobile || (isMobile && mobileStep === 3)) && (<ChoiceRow>
                <Box style={{display: 'flex', flexDirection: 'column'}}>
                  {!isMobile && (<Text
                    text={'QUANDO VUOI DONARE?'}
                    color={theme.palette.text.secondary}
                    fontFamily={FONTS.MONTSERRAT}
                    fontWeight={700}
                    fontSize={scale(24)}
                    sx={{textTransform: 'uppercase', alignSelf: 'flex-start'}}
                  />)}
                  {!isMobile && (<Box
                    sx={{
                      backgroundColor: theme.palette.text.secondary,
                      height: scale(2),
                      width: '100%',
                      marginTop: scale(10),
                    }}
                  />)}
                </Box>
                <DetailBand>
                  {!isMobile && (<Text
                    text={'Seleziona il turno che preferisci.'}
                    color={theme.palette.ceramic.contrastText}
                    fontFamily={FONTS.MONTSERRAT}
                    fontWeight={400}
                    fontSize={scale(20)}
                    sx={{alignSelf: 'flex-start', width: scale(280)}}
                  />)}
                  <TypeGroup>
                    {raccolta.fasceETurni.map((fasciaETurno, index) => {
                      const isMattina = fasciaETurno.fascia.amPm === 1;
                      const isPomeriggio = fasciaETurno.fascia.amPm === 2;

                      // Solo gestiamo mattina e pomeriggio (se vuoi altre fasce, adattalo)
                      if (!isMattina && !isPomeriggio) return null;

                      return (
                        <Box
                          key={fasciaETurno.fascia.id}
                          onClick={() => handleFasciaClick(index)}
                          sx={{
                            cursor: 'pointer',
                            transition: 'all 0.8s',
                            [theme.breakpoints.down('sm')]: {
                              display: 'flex',
                              backgroundColor: selectedFascia === index ? theme.palette.white.contrastText : theme.palette.grey[400],
                              height: scale(200),
                              width: scale(200),
                              alignItems: 'center',
                              justifyContent: 'center',
                              borderRadius: scale(20),
                              WebkitTapHighlightColor: 'transparent',  // iOS
                              touchAction: 'manipulation',              // Android e altri
                              userSelect: 'none',                       // Previene selezione testo (opzionale)
                              WebkitUserSelect: 'none',
                              MozUserSelect: 'none',
                              msUserSelect: 'none',
                            },
                          }}
                        >
                          <DateRowInfo
                            IconComponent={
                              <QueryBuilder
                                sx={{
                                  zIndex: 2,
                                  fontSize: scale(60),
                                  transform: isPomeriggio ? 'rotate(90deg)' : 'none',
                                }}
                                color="white"
                              />
                            }
                            bgColor={selectedFascia === index ? theme.palette.white.contrastText : theme.palette.grey[400]}
                            colorStyle={isMobile ? 'secondary' : "primary"}
                            firstRowText={isMattina ? "Mattina" : "Pomeriggio"}
                            secondRowText={getFasciaRange(raccolta, index)}
                            iconSx={{width: scale(90), height: scale(90), borderRadius: scale(18), transition: isMobile ? 'all 0.8s' : undefined}}
                            sx={{flexDirection: 'column', width: scale(170), alignItems: 'center', cursor: 'pointer'}}
                            textSx={{width: scale(170), textAlign: 'center', fontWeight: 700}}
                          />
                        </Box>
                      );
                    })}
                  </TypeGroup>
                </DetailBand>
              </ChoiceRow>)}
              <ButtonGroup>
                <ThemedButton
                  variant="contained"
                  disabled={isDisabled()}
                  onClick={() => {
                    storePartialDonorInfo()
                    if (!isMobile || (isMobile && mobileStep === 3)) {
                      navigate(buildPath(nextUrl))
                    }
                    switch (mobileStep) {
                      case 2:
                        selectedFascia === null ? setMobileStep(3) : navigate(buildPath(nextUrl));
                        break;
                    }
                  }}
                >
                  <ThemedText text={'conferma'} fontWeight={600} fontSize={scale(18)} fontFamily={FONTS.MONTSERRAT}/>
                </ThemedButton>
              </ButtonGroup>
            </DonorsWrapper>)}
          </ContentWrapper>
        </Base>
      </Wrapper>
    </>
  );
}

export default OperatoreAggiungiDonatore;
